import { useState } from 'react';
import { Shield, Lock, Eye, Book, Image, Download, X } from 'lucide-react';
import ContactModal from './ContactModal';

export default function LandingPage({ onSignIn, loading, error, onDismissError }) {
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  return (
    <div className="min-h-screen bg-[#F8F5F0] text-[#2D3436] font-serif relative overflow-x-hidden">
      {/* Tekstur overlay */}
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/felt.png')] z-[50]"></div>

      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[150] bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded shadow-lg max-w-sm w-[90%] font-sans text-sm">
          <p className="font-medium">{error}</p>
          <button
            onClick={onDismissError}
            className="text-red-500 hover:text-red-700 text-xs mt-1"
          >
            Lukk
          </button>
        </div>
      )}
      
      {/* Header */}
      <header className="w-full z-[100] sticky top-0 border-b border-[#D1CCC0] bg-[#F8F5F0]/95 backdrop-blur-md transition-all duration-300 pt-safe">
        <div className="max-w-5xl mx-auto px-6 py-5 flex justify-between items-center">
          <div className="flex items-center gap-4 group cursor-pointer">
            <div className="p-1 rounded-xl shadow-md transform -rotate-2 transition-all duration-500 group-hover:rotate-0 group-hover:shadow-xl bg-[#F8F5F0]">
              <img
                src="/logo-mark.webp"
                alt="FiskDa"
                width={44}
                height={44}
                className="w-11 h-11 object-contain block rounded-lg"
              />
            </div>
            <div className="flex flex-col">
              <h1 className="font-bold tracking-[0.05em] text-2xl leading-none italic text-[#3B4D3C] transition-colors duration-300 group-hover:text-[#E67E22]">
                FiskDa
              </h1>
              <span className="font-sans text-[8px] uppercase tracking-[0.4em] text-[#E67E22] font-black mt-1">
                Skitt fiske!
              </span>
            </div>
          </div>
          
          <button 
            onClick={onSignIn}
            disabled={loading}
            className="px-6 py-3 bg-white border border-[#D1CCC0] rounded shadow-sm hover:shadow-xl transition-all duration-300 font-sans text-sm font-semibold text-[#3B4D3C] hover:border-[#3B4D3C] hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {loading ? 'Laster...' : 'Logg inn'}
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="w-full max-w-4xl mx-auto px-6 py-16 space-y-32 z-10">
        
        {/* Hero */}
        <section className="text-center space-y-8 py-16 animate-in fade-in duration-1000">
          <h2 className="text-5xl md:text-7xl font-black italic text-[#3B4D3C] tracking-tight transition-all duration-500 hover:text-[#E67E22]">
            Hver fangst har en historie
          </h2>
          <p className="text-xl md:text-2xl text-[#2D3436]/70 max-w-2xl mx-auto leading-relaxed">
            Logg din neste fangst her
          </p>
          
          <button 
            onClick={onSignIn}
            disabled={loading}
            className="mt-8 px-12 py-5 bg-[#3B4D3C] text-[#F8F5F0] rounded shadow-xl font-black italic text-xl hover:bg-[#2D3A2D] hover:scale-105 hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100"
          >
            Kom i gang
          </button>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-8 pt-12">
            {[
              { icon: Shield, text: 'Din personlige fiskedatabase' },
              { icon: Lock, text: 'Ingen abonnement' },
              { icon: Eye, text: 'Full kontroll' }
            ].map((badge, i) => (
              <div 
                key={i}
                className="flex items-center gap-2 font-sans text-xs uppercase tracking-wider text-[#3B4D3C]/60 hover:text-[#3B4D3C] transition-all duration-300 hover:scale-110 cursor-default"
              >
                <badge.icon size={16} />
                <span>{badge.text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Display Modes */}
        <section className="space-y-12">
          <div className="text-center space-y-4">
            <h3 className="text-3xl md:text-4xl font-black italic text-[#3B4D3C]">
              Classic eller Clean
            </h3>
            <p className="text-lg text-[#2D3436]/70 max-w-xl mx-auto">
              Velg hvordan du vil se fangsten din.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Classic */}
            <div className="bg-white p-8 border border-[#D1CCC0] shadow-md space-y-4 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:border-[#3B4D3C]/30 group cursor-default">
              <div className="flex items-center gap-3 pb-4 border-b border-[#D1CCC0] transition-colors duration-300 group-hover:border-[#3B4D3C]/30">
                <Book size={24} className="text-[#3B4D3C] transition-transform duration-300 group-hover:scale-110" />
                <h4 className="font-black italic text-2xl text-[#3B4D3C] transition-colors duration-300 group-hover:text-[#E67E22]">Classic</h4>
              </div>
              <p className="font-sans text-sm text-[#2D3436]/70 leading-relaxed">
                Analog loggbok-visning med alle tekniske data. Vekt, mål, vær, utstyr – alt dokumentert som i en tradisjonell fangstjournal.
              </p>
            </div>

            {/* Clean */}
            <div className="bg-white p-8 border border-[#D1CCC0] shadow-md space-y-4 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:border-[#3B4D3C]/30 group cursor-default">
              <div className="flex items-center gap-3 pb-4 border-b border-[#D1CCC0] transition-colors duration-300 group-hover:border-[#3B4D3C]/30">
                <Image size={24} className="text-[#3B4D3C] transition-transform duration-300 group-hover:scale-110" />
                <h4 className="font-black italic text-2xl text-[#3B4D3C] transition-colors duration-300 group-hover:text-[#E67E22]">Clean</h4>
              </div>
              <p className="font-sans text-sm text-[#2D3436]/70 leading-relaxed">
                Moderne, ren visning med fokus på bildet. Perfekt for deling på sosiale medier uten å avsløre ditt hemmelige fiskested.
              </p>
            </div>
          </div>
        </section>

        {/* Digital Export */}
        <section className="space-y-8">
          <div className="text-center space-y-4">
            <h3 className="text-3xl md:text-4xl font-black italic text-[#3B4D3C]">
              Dine hemmeligheter er trygge hos oss
            </h3>
            <p className="text-lg text-[#2D3436]/70 max-w-xl mx-auto">
              Alt du logger er privat som standard. Du bestemmer selv hva du vil dele.
            </p>
          </div>

          <div className="bg-white p-8 md:p-12 border border-[#D1CCC0] shadow-md text-center space-y-6 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:border-[#3B4D3C]/30 group cursor-default">
            <Download size={48} className="mx-auto text-[#3B4D3C] transition-all duration-500 group-hover:scale-110 group-hover:text-[#E67E22]" strokeWidth={1.5} />
            <div className="space-y-4">
              <p className="font-sans text-sm text-[#2D3436]/70 max-w-lg mx-auto leading-relaxed">
                Dine fangster lagres trygt i din personlige database. Ingen andre kan se dem med mindre du velger å dele.
              </p>
              <p className="font-sans text-sm text-[#2D3436]/70 max-w-lg mx-auto leading-relaxed">
                Når du vil dele, eksporter fangsten som et optimalisert fangstkort. Del hvor du vil, når du vil – uten å avsløre dine hemmelige fiskesteder eller miste kontrollen over dine data.
              </p>
            </div>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="w-full border-t border-[#D1CCC0] bg-white/50 backdrop-blur-sm mt-32">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center space-y-6">
          <div className="flex flex-wrap justify-center gap-8 font-sans text-sm text-[#3B4D3C]/60">
            <button 
              onClick={() => setShowPrivacyModal(true)}
              className="hover:text-[#3B4D3C] transition-all duration-300 hover:scale-110"
            >
              Personvern
            </button>
            <button 
              onClick={() => setShowAboutModal(true)}
              className="hover:text-[#3B4D3C] transition-all duration-300 hover:scale-110"
            >
              Om FiskDa
            </button>
            <button 
              onClick={() => setShowContactModal(true)}
              className="hover:text-[#3B4D3C] transition-all duration-300 hover:scale-110"
            >
              Kontakt
            </button>
          </div>
          <p className="font-sans text-xs text-[#3B4D3C]/40 italic">
            Fungerer like bra på mobil og desktop
          </p>
        </div>
      </footer>

      {/* Kontakt Modal */}
      {showContactModal && (
        <ContactModal onClose={() => setShowContactModal(false)} />
      )}

      {/* Om FiskDa Modal */}
      {showAboutModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
          onClick={() => setShowAboutModal(false)}
        >
          <div
            className="bg-[#F8F5F0] border-2 border-[#3B4D3C] rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 relative animate-in fade-in duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAboutModal(false)}
              className="absolute top-4 right-4 text-[#3B4D3C]/60 hover:text-[#3B4D3C] transition-colors"
              aria-label="Lukk"
            >
              <X size={24} />
            </button>
            <h3 className="text-2xl font-black italic text-[#3B4D3C] mb-6">
              Om FiskDa
            </h3>
            
            <div className="font-sans text-sm text-[#2D3436]/80 leading-relaxed space-y-4">
              <p>
                FiskDa er en digital fiskebok laget for alle som elsker fisking — enten du er nybegynner eller fluefiskenerd. Alt starter med gleden ved å fiske og ønsket om å ta vare på minnene på en litt mer personlig måte.
              </p>
              
              <p>
                I stedet for å fylle notatbøker, kameraruller og tilfeldige mapper, får du alt samlet på ett sted, rett på telefonen din. Fangstene lagres trygt i din egen sky, og du kan laste dem ned når du vil.
              </p>
              
              <p>
                Ideen kom da en kompis som driver seriøst med fluefiske fortalte at han dokumenterte hver eneste fangst i elva. Da ble det klart: Det trengs en enkel, moderne og trygg måte å føre sin egen fiskebok på.
              </p>
              
              <p>
                FiskDa er fortsatt under utvikling, så alle tilbakemeldinger betyr mye og hjelper prosjektet å vokse i riktig retning.
              </p>
            </div>

            <button
              onClick={() => setShowAboutModal(false)}
              className="mt-8 w-full px-6 py-3 bg-[#3B4D3C] text-[#F8F5F0] rounded shadow-md font-sans font-semibold hover:bg-[#2D3A2D] transition-all duration-300"
            >
              Lukk
            </button>
          </div>
        </div>
      )}

      {/* Personvern Modal */}
      {showPrivacyModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
          onClick={() => setShowPrivacyModal(false)}
        >
          <div
            className="bg-[#F8F5F0] border-2 border-[#3B4D3C] rounded-lg shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-8 relative animate-in fade-in duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowPrivacyModal(false)}
              className="sticky top-0 float-right -mt-2 -mr-2 text-[#3B4D3C]/60 hover:text-[#3B4D3C] transition-colors bg-[#F8F5F0] rounded-full p-1 z-10"
              aria-label="Lukk"
            >
              <X size={24} />
            </button>
            <h3 className="text-2xl font-black italic text-[#3B4D3C] mb-2">
              Personvernerklæring for FiskDa
            </h3>
            <p className="font-sans text-xs text-[#2D3436]/60 mb-6">
              Sist oppdatert: 18. januar 2026 | Versjon: 1.0
            </p>
            
            <div className="font-sans text-sm text-[#2D3436]/80 leading-relaxed space-y-6">
              <section>
                <h4 className="font-bold text-[#3B4D3C] mb-2">1. Hvem er vi?</h4>
                <p className="mb-2">FiskDa er databehandlingsansvarlig for dine personopplysninger.</p>
                <p className="text-xs">E-post: tmfoght@gmail.com</p>
              </section>

              <section>
                <h4 className="font-bold text-[#3B4D3C] mb-2">2. Hvilke personopplysninger samler vi inn?</h4>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold">2.1 Fangstdata</p>
                    <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                      <li>Bilder av fisk (lagres kryptert i Google Firebase)</li>
                      <li>Artsinformasjon (fiskenavn og latinsk navn)</li>
                      <li>Fangstdetaljer (vekt, lengde, dato og tid)</li>
                      <li>Værdata (temperatur, vind, værforhold - valgfritt)</li>
                      <li>Utstyrsinformasjon (stang, snelle, agn - valgfritt)</li>
                      <li>Notater (fritekst om fangsten - valgfritt)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold">2.2 Posisjonsdata</p>
                    <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                      <li>GPS-koordinater (kun hvis du velger "Nøyaktig" personvernnivå)</li>
                      <li>Maskert posisjon (100m eller 1km område basert på ditt valg)</li>
                      <li>Generell region (kun fylke/landsdel for anonyme statistikker)</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold">2.3 Tekniske data</p>
                    <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                      <li>Bruker-ID (anonym Firebase-identifikator)</li>
                      <li>Enhetsinformasjon (enhetstype og nettleser)</li>
                      <li>Bruksstatistikk (kun hvis du samtykker)</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h4 className="font-bold text-[#3B4D3C] mb-2">3. Hvorfor behandler vi dine personopplysninger?</h4>
                <div className="space-y-2">
                  <p className="font-semibold">3.1 Rettslig grunnlag</p>
                  <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                    <li>Samtykke (GDPR art. 6.1.a) for lagring av fangstdata</li>
                    <li>Berettiget interesse (GDPR art. 6.1.f) for teknisk drift og sikkerhet</li>
                  </ul>
                  <p className="font-semibold mt-2">3.2 Formål</p>
                  <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                    <li>Lagre og organisere dine fangstregistreringer</li>
                    <li>Dele fangster med andre (kun med ditt samtykke)</li>
                    <li>Forbedre appen basert på anonyme bruksdata</li>
                  </ul>
                </div>
              </section>

              <section>
                <h4 className="font-bold text-[#3B4D3C] mb-2">4. Deling av personopplysninger</h4>
                <div className="space-y-2">
                  <p className="font-semibold">Vi deler ALDRI:</p>
                  <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                    <li>Nøyaktige GPS-koordinater uten ditt eksplisitte samtykke</li>
                    <li>Private notater eller sensitive detaljer</li>
                    <li>Personidentifiserbare opplysninger til tredjeparter</li>
                    <li>Data for markedsføring eller profilering</li>
                  </ul>
                  <p className="font-semibold mt-2">Tredjeparter vi bruker:</p>
                  <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                    <li>Google Firebase (sikker datalagring og autentisering)</li>
                  </ul>
                </div>
              </section>

              <section>
                <h4 className="font-bold text-[#3B4D3C] mb-2">5. Hvor lenge lagrer vi data?</h4>
                <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                  <li>Fangstdata: Til du sletter dem eller din konto</li>
                  <li>Delte fangster: 30 dager, deretter automatisk slettet</li>
                  <li>Anonyme analysedata: Maksimalt 2 år</li>
                  <li>Tekniske logger: Maksimalt 90 dager</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-[#3B4D3C] mb-2">6. Dine rettigheter (GDPR)</h4>
                <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                  <li>Rett til innsyn (art. 15) - be om en kopi av alle data vi har om deg</li>
                  <li>Rett til retting (art. 16) - endre eller oppdatere dine data</li>
                  <li>Rett til sletting (art. 17) - slette alle dine data permanent</li>
                  <li>Rett til dataportabilitet (art. 20) - eksportere data i JSON-format</li>
                  <li>Rett til å trekke tilbake samtykke (art. 7.3)</li>
                  <li>Rett til innsigelse (art. 21)</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-[#3B4D3C] mb-2">7. Sikkerhetstiltak</h4>
                <ul className="list-disc list-inside text-xs space-y-1 ml-2">
                  <li>Kryptering av all data både under overføring og lagring</li>
                  <li>Tilgangskontroll - kun du har tilgang til dine private data</li>
                  <li>Sikre API-er med autentiserte forbindelser</li>
                  <li>Regelmessige sikkerhetskopier</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-[#3B4D3C] mb-2">8. Klagerett</h4>
                <p className="text-xs">
                  Du har rett til å klage til Datatilsynet hvis du mener vi behandler dine personopplysninger i strid med personvernregelverket.
                </p>
                <p className="text-xs mt-2">
                  Datatilsynet: datatilsynet.no | Telefon: 22 39 69 00
                </p>
              </section>

              <section>
                <h4 className="font-bold text-[#3B4D3C] mb-2">9. Kontakt oss</h4>
                <p className="text-xs">E-post: tmfoght@gmail.com</p>
                <p className="text-xs mt-2 italic">Vi svarer på henvendelser innen 30 dager.</p>
              </section>
            </div>

            <button
              onClick={() => setShowPrivacyModal(false)}
              className="mt-8 w-full px-6 py-3 bg-[#3B4D3C] text-[#F8F5F0] rounded shadow-md font-sans font-semibold hover:bg-[#2D3A2D] transition-all duration-300"
            >
              Lukk
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
